import React, { useState } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { Badge } from './ui/badge';
import { ChevronDown } from 'lucide-react';

const ExpandableSection = ({
  title,
  children,
  icon: Icon,
  badge = null,
  defaultExpanded = false,
  helpText = '',
  required = false,
  disabled = false,
  className = ''
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleToggle = () => {
    if (!disabled) {
      setExpanded(!expanded);
    }
  };

  return (
    <Collapsible
      open={expanded}
      onOpenChange={handleToggle}
      disabled={disabled}
      className={`
        border rounded-lg mb-4
        ${expanded ? 'border-primary shadow-md' : 'border-border'}
        ${disabled ? 'opacity-60 bg-muted' : ''}
        ${className}
      `}
    >
      <CollapsibleTrigger
        className={`
          flex items-center justify-between w-full p-4 min-h-16
          ${expanded ? 'bg-primary/5 rounded-t-lg' : 'bg-muted/50 rounded-lg'}
          hover:bg-muted/70 transition-colors
          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
        `}
        disabled={disabled}
      >
        <div className="flex items-center gap-3 flex-1">
          {Icon && (
            <div className={expanded ? 'text-primary' : 'text-muted-foreground'}>
              <Icon className="h-5 w-5" />
            </div>
          )}

          <div className="flex-1 text-left">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`font-bold ${expanded ? 'text-primary' : 'text-foreground'}`}
              >
                {title}
                {required && (
                  <span className="text-destructive ml-1">*</span>
                )}
              </span>

              {badge !== null && badge !== undefined && (
                <Badge
                  variant={badge > 0 ? 'default' : 'outline'}
                  className="h-6 text-xs"
                >
                  {badge}
                </Badge>
              )}
            </div>

            {helpText && (
              <p className="text-xs text-muted-foreground mt-1 leading-tight">
                {helpText}
              </p>
            )}
          </div>
        </div>

        <ChevronDown
          className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
            expanded ? 'rotate-180' : ''
          }`}
        />
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="p-6 pt-4 border-t bg-background">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ExpandableSection;
